// Keeps the surgery waitlist in step with changes made by other users, off the back of the
// PushEventService poll.
//
// This used to reload, because the only way to read the waitlist was to page the whole practice's
// list. It now patches one entry, against the single-waitlist GET added for it. That matters more
// here than elsewhere: the list is held for the nav badge, so it is refreshed whether or not anyone
// has the waitlist modal open, and a whole-practice page walk on every waitlist edit was the most
// expensive thing the live updates did.
//
// One event costs one request and there is no cheaper floor - an update has to be read to know what
// it changed. The saving is in what is no longer fetched alongside it.
app.service("SurgeryWaitlistLiveUpdateService", [
	"SurgeryWaitlistAPI",
	"LiveReloadService",
	"PUSH_EVENTS",
	"LiveUpdateGuard",
	function (SurgeryWaitlistAPI, LiveReloadService, PUSH_EVENTS, LiveUpdateGuard) {
		/**
		 * Wires a view up to surgery waitlist push events for the life of its scope.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  waitlists() - the waitlist entries, patched in place
		 *              isRelevant() - whether this view is showing the waitlist at all
		 *              onChanged() - re-count the badge and re-render after a patch
		 */
		function subscribe(scope, spec) {
			// Waitlist ids with a fetch in flight. A second event for the same entry must not race the
			// first: two responses can land out of order and leave the older one displayed.
			const fetching = {};

			/** The entries, or null before the view's first load has landed. */
			function waitlists() {
				const list = spec.waitlists();
				return angular.isArray(list) ? list : null;
			}

			/** The index of an entry in the list, or -1. */
			function indexOf(list, surgeryWaitlistId) {
				for (let i = 0; i < list.length; i++) {
					if (LiveReloadService.sameId(list[i].surgery_waitlist_id, surgeryWaitlistId)) {
						return i;
					}
				}
				return -1;
			}

			function onUpsert(payload) {
				const list = waitlists();
				if (!list || !payload.surgeryWaitlistId || !payload.patientId || !spec.isRelevant()) {
					return;
				}
				if (fetching[payload.surgeryWaitlistId]) {
					return;
				}
				fetching[payload.surgeryWaitlistId] = true;

				// Through LiveUpdateGuard, which stops this after a few failures in a row.
				LiveUpdateGuard.run("surgeryWaitlist", function () {
					return SurgeryWaitlistAPI.getSurgeryWaitlist(payload.patientId, payload.surgeryWaitlistId);
				}).then(
					function (resp) {
						delete fetching[payload.surgeryWaitlistId];

						const waitlist = resp.data;
						if (!waitlist || !waitlist.surgery_waitlist_id) {
							return;
						}

						// Re-read the list: the view may have re-fetched while this was in flight.
						const current = waitlists();
						if (!current) {
							return;
						}

						const index = indexOf(current, waitlist.surgery_waitlist_id);
						if (index === -1) {
							current.push(waitlist);
						} else {
							current[index] = waitlist;
						}
						spec.onChanged();
					},
					function () {
						// Deliberately silent: the poll will not re-announce this change, so the list stays
						// as it is until the next one. Nothing is logged - the response carries patient data.
						delete fetching[payload.surgeryWaitlistId];
					}
				);
			}

			// There is no SURGERY_WAITLIST_DELETED type - a waitlist entry is completed rather than
			// removed, which arrives as an update.
			[PUSH_EVENTS.SURGERY_WAITLIST_ADDED, PUSH_EVENTS.SURGERY_WAITLIST_UPDATED].forEach(function (eventName) {
				scope.$on(eventName, function (e, pushEvent) {
					onUpsert((pushEvent && pushEvent.data) || {});
				});
			});
		}

		const service = {
			subscribe: subscribe,
		};

		return service;
	},
]);
